import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function HealthInsurancePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-emerald-600">Health Insurance</span>
            </h1>
            <p className="text-xl text-gray-600">
              Protect your savings from medical emergencies
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* What is Health Insurance */}
            <section className="bg-emerald-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Health Insurance is Critical</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Medical inflation in India is rising at 10-15% annually. A single hospitalization can cost ₹3-10 lakhs, 
                wiping out years of savings overnight. Health insurance ensures that you and your family get the best 
                medical treatment without financial stress.
              </p>
              <div className="bg-white rounded-xl p-6 border-2 border-emerald-200">
                <h3 className="font-bold text-lg mb-3">😱 Shocking Facts:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Average ICU cost in metro cities: ₹8-15 lakhs</li>
                  <li>• Heart surgery: ₹5-10 lakhs</li>
                  <li>• Cancer treatment: ₹10-30 lakhs</li>
                  <li>• Only 30% Indians have health insurance</li>
                </ul>
              </div>
            </section>

            {/* Types of Coverage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Coverage Options</h2>
              <div className="grid gap-6">
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-emerald-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-emerald-700">Individual Health Insurance</h3>
                  <p className="text-gray-600">Coverage for a single person. Suitable for bachelors or individuals needing personal coverage.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-emerald-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-emerald-700">Family Floater</h3>
                  <p className="text-gray-600">One policy covering entire family (spouse + children). Most cost-effective for families.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-emerald-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-emerald-700">Senior Citizen Plans</h3>
                  <p className="text-gray-600">Specialized coverage for parents above 60 years with pre-existing disease coverage.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-emerald-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-emerald-700">Super Top-Up Plans</h3>
                  <p className="text-gray-600">Additional coverage at lower premiums. Works after your base policy is exhausted.</p>
                </div>
              </div>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Cashless Treatment:</strong>
                    <span className="text-gray-700"> Get treated at 10,000+ network hospitals without paying upfront</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Pre & Post Hospitalization:</strong>
                    <span className="text-gray-700"> Cover for medical expenses 30-60 days before and after hospitalization</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Daycare Procedures:</strong>
                    <span className="text-gray-700"> Coverage for treatments that don't require 24-hour hospitalization</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">No Claim Bonus:</strong>
                    <span className="text-gray-700"> Sum insured increases by 10-50% every claim-free year</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Tax Benefits:</strong>
                    <span className="text-gray-700"> Deduction up to ₹25,000 (self) + ₹25,000 (parents) under Section 80D</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* Recommended Coverage */}
            <section className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">Recommended Coverage</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white text-gray-900 rounded-xl p-6">
                  <h3 className="font-bold mb-2">Metro Cities</h3>
                  <div className="text-3xl font-bold text-emerald-700 mb-2">₹10-15L</div>
                  <p className="text-sm text-gray-600">Per family minimum coverage</p>
                </div>
                <div className="bg-white text-gray-900 rounded-xl p-6">
                  <h3 className="font-bold mb-2">Tier 2/3 Cities</h3>
                  <div className="text-3xl font-bold text-emerald-700 mb-2">₹5-10L</div>
                  <p className="text-sm text-gray-600">Per family minimum coverage</p>
                </div>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-green-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Get Health Insurance Through Us?</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Compare plans from all major insurers - HDFC Ergo, Star Health, Care, etc.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Expert help in understanding policy terms and exclusions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Complete claims support - we handle all paperwork and follow-ups</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Annual policy review to ensure adequate coverage as family grows</span>
                </li>
              </ul>
            </section>

            {/* Licensed Advisor Badge */}
            <section className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-8">
              <div className="flex items-start gap-6">
                <div className="bg-emerald-600 rounded-xl p-4 flex-shrink-0">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Licensed Health Insurance Advisor</h3>
                  <p className="text-gray-700 mb-4">
                    K Satish is officially licensed with Care Health Insurance Ltd, ensuring you receive 
                    professional and compliant health insurance advice.
                  </p>
                  <div className="bg-white rounded-lg p-4 border-2 border-emerald-200">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">Insurance Company:</p>
                        <p className="text-sm font-bold text-emerald-800">Care Health Insurance Ltd</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">License Code:</p>
                        <p className="text-lg font-mono font-bold text-emerald-700">RLH20481559</p>
                      </div>
                    </div>
                    <a
                      href="https://www.careinsurance.com/hp/SATEESHKOTTA12"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-all font-semibold text-sm"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verify My Profile on Care Insurance
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">Click to verify my credentials on official Care Insurance website</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center bg-emerald-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Don't Wait for a Medical Emergency
              </h2>
              <p className="text-emerald-100 mb-6">
                Get comprehensive health insurance for your family today
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-emerald-700 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Get Free Quote
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I need Health Insurance for my family"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
